import React from 'react';
import { StyleSheet, View } from 'react-native';
import { UserRole, UserStatus } from '@alucio/aws-beacon-amplify/src/models';
import { DNAButton, DNAText, GridList, luxColors, DNAChipStatus } from '@alucio/lux-ui';
import { DNAChip, DNAUserContextMenu } from 'src/components/DNA';
import { DRAWER_ENTITIES, drawerActions } from 'src/state/redux/slice/drawer';
import { useDispatch } from 'src/state/redux';
import { UserORM } from 'src/types/orms';

const styles = StyleSheet.create({
  column: {
    paddingVertical: 12,
  },
  itemsWrapper: {
    flex: 1,
    flexDirection: 'column',
  },
  itemTextColor: {
    color: luxColors.contentText.tertiary,
  },
});

export const MAPPED_ROLES = {
  [UserRole.TENANT_PUBLISHER]: 'Publisher',
  [UserRole.TENANT_VIEWER]: 'Viewer',
  [UserRole.TENANT_VIEW_ONLY]: 'View Only',
};

export const USER_STATUS_VARIANT: {[key in UserStatus]: DNAChipStatus} = {
  [UserStatus.ACTIVE]: 'success',
  [UserStatus.DEACTIVATED]: 'secondary',
};

interface Props {
  user: UserORM;
}

const DNAUserDefaultRow = (props: Props) => {
  const { user } = props;
  const dispatch = useDispatch();

  const filters = user?.meta.customFilterValues.defaultFilters ?? []
  const defaultFilterValues = Object.values(filters).map(filters => filters.displayValues).flat().sort()

  function openEditModal(): void {
    dispatch(drawerActions.toggle({
      entityId: user.model.id,
      entity: DRAWER_ENTITIES.USER,
    }));
  }

  return (
    <GridList.Row
      interactive="hoverable"
      hoverColor={luxColors.backgroundColor.secondary}
      onPress={openEditModal}
    >
      <GridList.Col style={[styles.column, { justifyContent: 'center' }]} span={1}>
        <DNAUserContextMenu user={user.model}>
          <DNAButton
            appearance="ghost"
            status="subtle"
            iconLeft="dots-three-vertical-bold"
          />
        </DNAUserContextMenu>
      </GridList.Col>
      <GridList.Col style={styles.column} span={2}>
        <DNAText numberOfLines={1} style={styles.itemTextColor}>
          { `${user.model.givenName} ${user.model.familyName}` }
        </DNAText>
      </GridList.Col>
      <GridList.Col style={styles.column} span={1}>
        <DNAText style={styles.itemTextColor}>{MAPPED_ROLES[user.model.role] ?? '(Invalid Role)'}</DNAText>
      </GridList.Col>
      <GridList.Col style={styles.column} span={3}>
        <View style={styles.itemsWrapper}>
          <DNAText style={styles.itemTextColor} numberOfLines={1}>{user.model.email}</DNAText>
        </View>
      </GridList.Col>
      <GridList.Col style={styles.column} span={3}>
        <DNAText numberOfLines={3} style={styles.itemTextColor}>{ defaultFilterValues.join(', ') }</DNAText>
      </GridList.Col>
      <GridList.Col style={styles.column} span={2}>
        <DNAChip.Beacon status={USER_STATUS_VARIANT[user.model.status!]} children={user.model.status} />
      </GridList.Col>
      <GridList.Col style={styles.column} span={1}>
        <View style={styles.itemsWrapper}>
          {
            user.model.shareCC?.map((email, idx) => (
              <DNAText key={`${email}${idx}`} style={styles.itemTextColor} numberOfLines={1}>{email}</DNAText>
            ))
          }
        </View>
      </GridList.Col>
      <GridList.Col style={styles.column} span={1}>
        <View style={styles.itemsWrapper}>
          {
            user.model.shareBCC?.map((email, idx) => (
              <DNAText key={`${email}${idx}`} style={styles.itemTextColor} numberOfLines={1}>{email}</DNAText>
            ))
          }
        </View>
      </GridList.Col>
    </GridList.Row>
  );
};

DNAUserDefaultRow.displayName = 'DNAUserDefaultRow';

export default DNAUserDefaultRow;
